import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createTeamShareLink,
  listTeamShareLinks,
  revokeTeamShareLink,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
export function useGuestLinks(teamId: string) {
  const viewer = useAuthStore((s) => s.me?.user?.id);
  const host = useAuthStore((s) => s.me?.current_club?.id);
  const client = useQueryClient();
  const key = unscopedKey("team-share-links", { teamId, viewer, host });
  const refresh = () => client.invalidateQueries({ queryKey: key });
  const list = useQuery({
    queryKey: key,
    queryFn: () => listTeamShareLinks(teamId),
    enabled: !!viewer && !!teamId,
  });
  const create = useMutation({
    mutationFn: (input: Parameters<typeof createTeamShareLink>[1]) =>
      createTeamShareLink(teamId, input),
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeTeamShareLink(teamId, id),
    onSuccess: refresh,
  });
  return { list, create, revoke, identity: `${host}:${viewer}:${teamId}` };
}

/** Aggregate only teams already authorized by the club-admin team endpoint.
 * Reuse the exact per-team metadata cache so create/revoke refresh the history. */
export function useClubGuestLinks(
  teams: readonly { id: string; name: string }[],
) {
  const viewer = useAuthStore((s) => s.me?.user?.id);
  const host = useAuthStore((s) => s.me?.current_club?.id);
  const queries = useQueries({
    queries: teams.map((team) => ({
      queryKey: unscopedKey("team-share-links", {
        teamId: team.id,
        viewer,
        host,
      }),
      queryFn: () => listTeamShareLinks(team.id),
      enabled: !!viewer,
    })),
  });
  return {
    links: queries
      .flatMap((query, index) =>
        (query.data?.links ?? []).map((link) => ({
          ...link,
          teamName: teams[index].name,
        })),
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    loading: queries.some((q) => q.isLoading),
    error: queries.some((q) => q.isError),
    retry: () =>
      Promise.all(queries.filter((q) => q.isError).map((q) => q.refetch())),
  };
}
